import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Syringe, Search, Eye } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const VaccinationRecords = () => {
  const navigate = useNavigate();
  const [vaccinations, setVaccinations] = useState([]);
  const [pets, setPets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [vaccResp, petsResp, ownersResp] = await Promise.all([
        api.get('/vaccinations'),
        api.get('/pets'),
        api.get('/owners'),
      ]);
      setVaccinations(vaccResp.data);
      setPets(petsResp.data);
      setOwners(ownersResp.data);
    } catch (error) {
      toast.error('Failed to load vaccination records');
    } finally {
      setLoading(false);
    }
  };

  const getPet = (petId) => pets.find((p) => p.id === petId);
  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || '—';

  const filtered = vaccinations.filter((v) => {
    const pet = getPet(v.pet_id);
    const q = search.toLowerCase();
    return (
      v.vaccine_name.toLowerCase().includes(q) ||
      v.manufacturer.toLowerCase().includes(q) ||
      v.batch_number.toLowerCase().includes(q) ||
      pet?.name?.toLowerCase().includes(q) ||
      getOwnerName(pet?.owner_id).toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading vaccination records...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold text-sage-800 mb-2">Vaccination Records</h1>
          <p className="text-gray-600">All vaccination records across visits</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by vaccine, pet, owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-lg p-12 text-center text-gray-500">
          <Syringe className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          {search ? 'No records match your search.' : 'No vaccination records found.'}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100">
            {filtered.map((v) => {
              const pet = getPet(v.pet_id);
              const isOverdue = new Date(v.next_due_date) < new Date();
              return (
                <div key={v.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sage-800">{pet?.name || '—'}</p>
                      <p className="text-sm text-gray-500">{getOwnerName(pet?.owner_id)}</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full flex-shrink-0" onClick={() => navigate(`/dashboard/visits/${v.visit_id}`)}>
                      <Eye className="w-4 h-4 mr-1" />Visit
                    </Button>
                  </div>
                  <p className="text-sm font-medium text-gray-800">{v.vaccine_name}</p>
                  <p className="text-xs text-gray-500">{v.manufacturer} · {v.batch_number}</p>
                  <div className="flex gap-4 mt-2">
                    <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-green-700'}`}>
                      Due: {new Date(v.next_due_date).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-gray-400">Given: {new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table view */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pet</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Vaccine</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Batch No.</TableHead>
                  <TableHead>Next Due</TableHead>
                  <TableHead>Date Given</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => {
                  const pet = getPet(v.pet_id);
                  return (
                    <TableRow key={v.id} className="hover:bg-sage-50 transition-colors">
                      <TableCell className="font-semibold text-sage-800">
                        {pet?.name || '—'}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {getOwnerName(pet?.owner_id)}
                      </TableCell>
                      <TableCell className="font-medium">{v.vaccine_name}</TableCell>
                      <TableCell className="text-gray-600">{v.manufacturer}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{v.batch_number}</TableCell>
                      <TableCell>
                        <span
                          className={`text-sm font-medium ${
                            new Date(v.next_due_date) < new Date()
                              ? 'text-red-600'
                              : 'text-green-700'
                          }`}
                        >
                          {new Date(v.next_due_date).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {new Date(v.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => navigate(`/dashboard/visits/${v.visit_id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Visit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
